import { GridColDef } from "@mui/x-data-grid-pro";
import { Box, GKDataGrid, Typography } from "components";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { OrderBatchRow, OrderBatchStatus } from "store/features/MFOrders";
import { useAppSelector } from "store/store";
import { extraColumnsField } from "utils/helpers";

interface Props {
  orders: OrderBatchRow[];
  count: number;
  loading?: boolean;
}

const STATUS_STYLE: Record<
  OrderBatchStatus,
  { bg: string; color: string; label: string }
> = {
  in_process: { bg: "#f9f2dd", color: "#e6b532", label: "In-Process" },
  completed: { bg: "#e8f3ec", color: "#15803d", label: "Completed" },
  partially_completed: {
    bg: "#e8f3ec",
    color: "#15803d",
    label: "Part Completed",
  },
  failed: { bg: "#fde8e8", color: "#c35245", label: "Failed" },
  cancelled: { bg: "#ececec", color: "#697586", label: "Cancelled" },
};

const StatusPill = ({ status }: { status: OrderBatchStatus }) => {
  const styles = STATUS_STYLE[status] ?? {
    bg: "#ececec",
    color: "#697586",
    label: status,
  };
  return (
    <Box
      sx={{
        alignItems: "center",
        bgcolor: styles.bg,
        border: "1px solid var(--white)",
        borderRadius: "6px",
        display: "inline-flex",
        px: "12px",
        py: "6px",
      }}
    >
      <Typography
        color={styles.color}
        fontSize={10}
        fontWeight={600}
        sx={{ letterSpacing: "0.3px", textTransform: "uppercase" }}
      >
        {styles.label}
      </Typography>
    </Box>
  );
};

const capitalize = (s: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ") : "";

const formatTransactionType = (t?: string) => {
  if (!t) return "";
  if (t === "lumpsum") return "Lumpsum";
  if (t === "sip") return "Plan (SIP)";
  if (t === "swp") return "Plan (SWP)";
  if (t === "stp") return "Plan (STP)";
  return capitalize(t);
};

const OrdersListTable = (props: Props) => {
  const { orders, loading = false, count } = props;
  const navigate = useNavigate();

  const companies = useAppSelector((state) => state.app.companies);

  const schemeNameByGcode = useMemo(() => {
    const map = new Map<string, string>();
    (companies ?? []).forEach((c: any) => {
      if (c?.gridCode && c?.name) {
        map.set(c.gridCode, c.name);
      }
    });
    return map;
  }, [companies]);

  const columns: GridColDef<OrderBatchRow>[] = [
    {
      ...extraColumnsField("Order Date & Time", "createdAt", 220, 0),
      renderCell: (params) => (
        <Typography
          color="var(--black-900)"
          fontSize={14}
          fontWeight={500}
          sx={{ whiteSpace: "nowrap" }}
        >
          {params?.row?.createdAt
            ? dayjs(params.row.createdAt).format("D MMM YYYY, hh:mm A")
            : "-"}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Basket / Scheme Name", "gcode", 320, 1),
      renderCell: (params) => {
        const name =
          (params?.row?.gcode && schemeNameByGcode.get(params.row.gcode)) ||
          params?.row?.gcode ||
          "-";
        return (
          <Typography
            color="var(--black-900)"
            fontSize={14}
            fontWeight={500}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            Scheme / {name}
          </Typography>
        );
      },
    },
    {
      ...extraColumnsField("Order Type", "orderType", 200, 1),
      renderCell: (params) => (
        <Typography
          color="var(--black-900)"
          fontSize={14}
          fontWeight={500}
          sx={{ whiteSpace: "nowrap" }}
        >
          {capitalize(params?.row?.orderType ?? "")} /{" "}
          {formatTransactionType(params?.row?.transactionType)}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Portfolios", "itemCount", 132, 0),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <Typography
          color="var(--black-900)"
          fontSize={14}
          fontWeight={500}
          sx={{ textAlign: "right" }}
        >
          {params?.row?.itemCount ?? 0}
        </Typography>
      ),
    },
    {
      ...extraColumnsField("Order Status", "status", 150, 0),
      align: "right",
      headerAlign: "right",
      renderCell: (params) =>
        params?.row?.status ? <StatusPill status={params.row.status} /> : null,
    },
  ];

  return (
    <GKDataGrid
      name="mfOrderBatches"
      loading={loading}
      columns={columns}
      rows={orders || []}
      totalCount={count}
      maxHeight={"calc(100vh - 327px)"}
      columnsButton={false}
      onRowClick={(params) => navigate(`/app/orders/${params.row.id}`)}
      sx={{
        "& .MuiDataGrid-row": { cursor: "pointer" },
      }}
    />
  );
};

export default OrdersListTable;
